import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { useAuth } from "./useAuth";
import { toastUtils } from "@/lib/toast-utils";

interface CancelBookingParams {
  bookingId: number;
  cancelReason: string;
}

interface CancelBookingResponse {
  success: boolean;
  booking?: unknown;
  tossData?: unknown;
  refundAmount?: number;
  cancelFee?: number;
  message?: string;
  error?: string;
}

export const useBookingCancel = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { supabase } = useSupabaseConnection();

  return useMutation({
    mutationFn: async (params: CancelBookingParams): Promise<CancelBookingResponse> => {
      if (!user) {
        throw new Error('로그인이 필요합니다.');
      }

      const { data, error } = await supabase.functions.invoke("cancel-payment", {
        body: { 
          bookingId: params.bookingId, 
          cancelReason: params.cancelReason, 
          userId: user.id 
        },
      });

      if (error) {
        console.error("Edge Function 호출 오류:", error);
        throw new Error(error.message || '취소 요청 중 오류가 발생했습니다.');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      return data;
    },
    onSuccess: (data) => {
      toastUtils.success({
        title: "예약 취소 완료",
        description: data.message || `환불 금액: ${data.refundAmount?.toLocaleString()}원`,
      });

      // 관련 쿼리 무효화하여 최신 데이터 로드
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking-detail'] });
    },
    onError: (error: Error) => {
      console.error("예약 취소 실패:", error);
      toastUtils.error({
        title: "취소 실패",
        description: error.message,
      });
    },
  });
};

// 취소 수수료 계산 함수
export const calculateCancelFee = (totalPrice: number, departureDate: string): { cancelFee: number; refundAmount: number } => {
  const departureTime = new Date(departureDate);
  const now = new Date();
  const daysDiff = Math.ceil((departureTime.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const hoursDiff = (departureTime.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  let cancelFee = 0;
  
  if (daysDiff >= 4) {
    // D-4 이전: 무료 취소 (100% 환불)
    cancelFee = 0;
  } else if (daysDiff >= 1) {
    // D-3 ~ D-1: 시간 체크 필요
    const currentHour = now.getHours();
    if (daysDiff > 1 || (daysDiff === 1 && currentHour < 12)) {
      // D-3 ~ D-1 오전: 30% 수수료 (70% 환불)
      cancelFee = Math.floor(totalPrice * 0.3);
    } else {
      // D-1 오후: 환불 불가
      cancelFee = totalPrice;
    }
  } else {
    // 당일: 환불 불가
    cancelFee = totalPrice;
  }
  
  const refundAmount = totalPrice - cancelFee;
  
  return { cancelFee, refundAmount };
};